import {
  Button,
  TextInputField,
  TextAreaField,
  useModalState,
  Modal,
  Banner,
  IconPlus,
  Flex
} from '@sequencehq/core-components'
import { useState } from 'react'
import { useForm, type FormFields } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'

type AnrokCategoryFields = FormFields<{
  name: string
  description: string
}>

type FormValues = {
  name: AnrokCategoryFields['name']['value']
  description: AnrokCategoryFields['description']['value']
}

interface Props {
  onCreate: (values: {
    taxCategoryName: string
    description: string
  }) => Promise<void>
}

const AddTaxCategoryForAnrokModalUI = ({
  onCreate,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      name: '',
      description: ''
    },
    fieldConfiguration: [
      {
        property: 'name',
        validation: [required]
      },
      {
        property: 'description',
        validation: []
      }
    ]
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const canSubmit = queries.isValid && !isSubmitting

  const handleSubmit = async (formValues: FormValues) => {
    if (formValues.name) {
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        await onCreate({
          taxCategoryName: formValues.name,
          description: formValues.description
        })
        onClose()
      } catch (error) {
        console.error('Failed to create Anrok tax category:', error)
        setSubmitError(
          getApiErrorMessage(error, 'Failed to create tax category')
        )
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Add tax category
      </Modal.Header>

      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap="16px">
          {submitError && (
            <Banner variant="error" data-testid="addTaxCategory.submitError">
              {submitError}
            </Banner>
          )}
          <TextInputField
            label="Name"
            data-testid="anrokTaxCategoryModal.name"
            value={fields.name.value}
            validationErrors={fields.name.validationErrors}
            onChange={e => fields.name.onChange(e.target.value)}
            placeholder="e.g. Software"
            autoFocus
          />
          <TextAreaField
            label="Description"
            data-testid="anrokTaxCategoryModal.description"
            value={fields.description.value}
            validationErrors={fields.description.validationErrors}
            onChange={fields.description.onChange}
            placeholder="Tax category description"
          />
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            void handleSubmit(queries.formData)
          }}
          data-testid="anrokTaxCategoryModal.submit"
        >
          {isSubmitting ? 'Creating...' : 'Create tax category'}
        </Button>
      </Modal.Footer>
    </>
  )
}

const AddTaxCategoryForAnrokModal = (props: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <Button
        variant="ghost"
        leadingIcon={<IconPlus color="inherit" />}
        onClick={openModal}
        data-testid="anrokTaxCategoryModal.trigger"
      >
        New category
      </Button>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="addTaxCategoryModal"
      >
        <AddTaxCategoryForAnrokModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default AddTaxCategoryForAnrokModal
